import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import type { Tag } from "@/types";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";

const PRESET_COLORS = ["#E8613C", "#2C5F8A", "#5B3E8A", "#2A7A4B", "#aaaacc", "#555577"];

export default function Tags() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [nome, setNome] = useState("");
  const [colore, setColore] = useState(PRESET_COLORS[0]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function load() {
    setLoading(true);
    api.get<Tag[]>("/admin/tags").then(setTags).finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function add(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null); setSaving(true);
    try {
      await api.post("/admin/tags", { nome: nome.trim(), colore });
      setNome(""); setColore(PRESET_COLORS[0]);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function del(id: string, name: string) {
    if (!confirm(`Eliminare il tag "${name}"? I contatti restano, perdono solo l'etichetta.`)) return;
    await api.delete(`/admin/tags/${id}`); load();
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-5">
      <header className="flex justify-between items-baseline">
        <h1 className="text-3xl font-serif text-primary">Tag</h1>
        <Link to="/admin/dashboard" className="font-mono text-[17px] text-secondary hover:text-primary">
          ← dashboard
        </Link>
      </header>

      <Card>
        <h2 className="font-mono text-xs uppercase tracking-wider text-secondary mb-3">
          Nuovo tag
        </h2>
        <form onSubmit={add} className="space-y-3">
          <Input label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} required
            placeholder='es. "Classe 2024-A"' />
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-secondary mb-1.5">
              Colore
            </div>
            <div className="flex gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColore(c)}
                  style={{ backgroundColor: c }}
                  className={`w-8 h-8 rounded-full border-2 ${colore === c ? "border-primary" : "border-transparent"}`}
                />
              ))}
            </div>
          </div>
          {error && <div className="text-red-300 text-sm">{error}</div>}
          <Button type="submit" loading={saving}>Crea tag</Button>
        </form>
      </Card>

      <Card>
        <h2 className="font-mono text-xs uppercase tracking-wider text-secondary mb-3">
          Tag esistenti ({tags.length})
        </h2>
        {loading ? (
          <div className="flex justify-center py-6"><Spinner size={24} /></div>
        ) : tags.length === 0 ? (
          <p className="text-secondary text-sm">Nessun tag. Creane uno per filtrare i contatti.</p>
        ) : (
          <div className="space-y-2">
            {tags.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <Badge color={t.colore}>{t.nome}</Badge>
                <span className="text-xs text-muted font-mono ml-auto">
                  {t.nContatti ?? 0} contatti
                </span>
                <button onClick={() => del(t.id, t.nome)}
                  className="bg-card border border-border text-secondary px-3 py-1.5 rounded-lg font-mono text-xs hover:border-red-500 hover:text-red-300">
                  Elimina
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
